// scheduleWindow.js — client-local schedule windows for Ai calendar surfaces

function cleanString(value) {
  return String(value || '').trim();
}

function normalizeTimeZone(value) {
  const timeZone = cleanString(value);
  if (!timeZone) return 'UTC';

  try {
    Intl.DateTimeFormat(undefined, { timeZone }).format(new Date());
    return timeZone;
  } catch (error) {
    return 'UTC';
  }
}

function normalizeLabel(value) {
  return cleanString(value).toLowerCase();
}

const MONTH_NAME_PATTERN = /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i;

const MONTH_NAME_TO_INDEX = Object.freeze({
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
});

function resolveNamedMonthIndexFromQuestion(question) {
  const match = normalizeLabel(question).match(MONTH_NAME_PATTERN);
  const token = match?.[1] ? cleanString(match[1]).toLowerCase() : '';
  if (!token) return null;
  const monthIndex = MONTH_NAME_TO_INDEX[token];
  return monthIndex === undefined ? null : monthIndex;
}

function buildNamedMonthScheduleWindow(question, now, timeZone) {
  const monthIndex = resolveNamedMonthIndexFromQuestion(question);
  if (monthIndex == null) return null;

  const explicitYearMatch = normalizeLabel(question).match(/\b(20\d{2})\b/);
  const year = explicitYearMatch?.[1]
    ? Number(explicitYearMatch[1])
    : readDateTimePart(getZonedDateTimeParts(now, timeZone), 'year');

  const month = monthIndex + 1;
  return {
    windowStartMs: zonedDateTimeToUtcMs({
      year,
      month,
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    }, timeZone),
    windowEndMs: endOfMonthMsForTimeZone(
      new Date(zonedDateTimeToUtcMs({
        year,
        month,
        day: 15,
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
      }, timeZone)),
      timeZone,
    ),
    timeZone,
  };
}

function inferQuestionFromMonthTitle(title) {
  const normalized = normalizeLabel(title);
  if (!normalized) return null;
  if (!MONTH_NAME_PATTERN.test(normalized)) return null;
  return `Show my ${cleanString(title)} schedule`;
}

function readDateTimePart(parts, type) {
  const match = parts.find((part) => part.type === type);
  return Number(match?.value || 0);
}

function getZonedDateTimeParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  return formatter.formatToParts(date);
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const asUtc = Date.UTC(
    readDateTimePart(parts, 'year'),
    readDateTimePart(parts, 'month') - 1,
    readDateTimePart(parts, 'day'),
    readDateTimePart(parts, 'hour'),
    readDateTimePart(parts, 'minute'),
    readDateTimePart(parts, 'second'),
  );

  return asUtc - date.getTime();
}

function zonedDateTimeToUtcMs({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
}, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const offset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
  return utcGuess - offset;
}

function startOfDayMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: readDateTimePart(parts, 'month'),
    day: readDateTimePart(parts, 'day'),
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

function endOfDayMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: readDateTimePart(parts, 'month'),
    day: readDateTimePart(parts, 'day'),
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  }, timeZone);
}

function startOfMonthMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: readDateTimePart(parts, 'month'),
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

function endOfMonthMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const year = readDateTimePart(parts, 'year');
  const month = readDateTimePart(parts, 'month');
  const firstOfNextMonthMs = zonedDateTimeToUtcMs({
    year: month === 12 ? year + 1 : year,
    month: month === 12 ? 1 : month + 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);

  return firstOfNextMonthMs - 1;
}

function addDaysInTimeZone(date, dayCount, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const noonUtc = zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: readDateTimePart(parts, 'month'),
    day: readDateTimePart(parts, 'day') + dayCount,
    hour: 12,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);

  return new Date(noonUtc);
}

function getDayOfWeekInTimeZone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' });
  const day = formatter.format(date);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[day] ?? 0;
}

function startOfWeekMsForTimeZone(date, timeZone, weekStartsOn = 0) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const dayOfWeek = getDayOfWeekInTimeZone(date, safeTimeZone);
  const daysFromWeekStart = (dayOfWeek - weekStartsOn + 7) % 7;
  const weekStartDate = addDaysInTimeZone(date, -daysFromWeekStart, safeTimeZone);
  return startOfDayMsForTimeZone(weekStartDate, safeTimeZone);
}

function buildThisWeekScheduleWindow(now, timeZone, weekStartsOn = 0) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const windowStartMs = startOfWeekMsForTimeZone(now, safeTimeZone, weekStartsOn);
  const weekStartDate = new Date(windowStartMs);
  const weekEndDate = addDaysInTimeZone(weekStartDate, 6, safeTimeZone);

  return {
    windowStartMs,
    windowEndMs: endOfDayMsForTimeZone(weekEndDate, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function buildNextWeekScheduleWindow(now, timeZone, weekStartsOn = 0) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const thisWeekStartMs = startOfWeekMsForTimeZone(now, safeTimeZone, weekStartsOn);
  const nextWeekStartDate = addDaysInTimeZone(new Date(thisWeekStartMs), 7, safeTimeZone);
  const windowStartMs = startOfDayMsForTimeZone(nextWeekStartDate, safeTimeZone);
  const weekEndDate = addDaysInTimeZone(nextWeekStartDate, 6, safeTimeZone);

  return {
    windowStartMs,
    windowEndMs: endOfDayMsForTimeZone(weekEndDate, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function addMonthsInTimeZone(date, monthCount, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  let year = readDateTimePart(parts, 'year');
  let month = readDateTimePart(parts, 'month') + monthCount;

  while (month > 12) {
    month -= 12;
    year += 1;
  }
  while (month < 1) {
    month += 12;
    year -= 1;
  }

  return new Date(zonedDateTimeToUtcMs({
    year,
    month,
    day: 15,
    hour: 12,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone));
}

function buildNextMonthScheduleWindow(now, timeZone) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const nextMonthAnchor = addMonthsInTimeZone(now, 1, safeTimeZone);

  return {
    windowStartMs: startOfMonthMsForTimeZone(nextMonthAnchor, safeTimeZone),
    windowEndMs: endOfMonthMsForTimeZone(nextMonthAnchor, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function startOfYearMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

function endOfYearMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: 12,
    day: 31,
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  }, timeZone);
}

function startOfQuarterMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const month = readDateTimePart(parts, 'month');
  const quarterStartMonth = (Math.floor((month - 1) / 3) * 3) + 1;

  return zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: quarterStartMonth,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);
}

function endOfQuarterMsForTimeZone(date, timeZone) {
  const parts = getZonedDateTimeParts(date, timeZone);
  const month = readDateTimePart(parts, 'month');
  const quarterEndMonth = (Math.floor((month - 1) / 3) * 3) + 3;
  const quarterEndAnchor = zonedDateTimeToUtcMs({
    year: readDateTimePart(parts, 'year'),
    month: quarterEndMonth,
    day: 15,
    hour: 12,
    minute: 0,
    second: 0,
    millisecond: 0,
  }, timeZone);

  return endOfMonthMsForTimeZone(new Date(quarterEndAnchor), timeZone);
}

function buildThisYearScheduleWindow(now, timeZone) {
  const safeTimeZone = normalizeTimeZone(timeZone);

  return {
    windowStartMs: startOfYearMsForTimeZone(now, safeTimeZone),
    windowEndMs: endOfYearMsForTimeZone(now, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function buildNextYearScheduleWindow(now, timeZone) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const nextYearAnchor = addMonthsInTimeZone(now, 12, safeTimeZone);

  return {
    windowStartMs: startOfYearMsForTimeZone(nextYearAnchor, safeTimeZone),
    windowEndMs: endOfYearMsForTimeZone(nextYearAnchor, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function buildThisQuarterScheduleWindow(now, timeZone) {
  const safeTimeZone = normalizeTimeZone(timeZone);

  return {
    windowStartMs: startOfQuarterMsForTimeZone(now, safeTimeZone),
    windowEndMs: endOfQuarterMsForTimeZone(now, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function buildNextQuarterScheduleWindow(now, timeZone) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const nextQuarterAnchor = addMonthsInTimeZone(now, 3, safeTimeZone);

  return {
    windowStartMs: startOfQuarterMsForTimeZone(nextQuarterAnchor, safeTimeZone),
    windowEndMs: endOfQuarterMsForTimeZone(nextQuarterAnchor, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

function buildUpcomingScheduleWindow(now, timeZone, dayCount = 7) {
  const safeTimeZone = normalizeTimeZone(timeZone);
  const windowStartMs = startOfDayMsForTimeZone(now, safeTimeZone);
  const endDate = addDaysInTimeZone(now, Math.max(0, dayCount - 1), safeTimeZone);

  return {
    windowStartMs,
    windowEndMs: endOfDayMsForTimeZone(endDate, safeTimeZone),
    timeZone: safeTimeZone,
  };
}

export function resolveClientTimeZone() {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch (error) {
    return 'UTC';
  }
}

export function inferScheduleQuestionFromPlan(plan) {
  const title = cleanString(plan?.presentation?.view?.title);
  const titleLower = title.toLowerCase();
  const calendarSubView = cleanString(plan?.presentation?.view?.calendarSubView).toUpperCase();
  const monthQuestion = inferQuestionFromMonthTitle(title);

  if (titleLower === 'today') return "What's on my plate today?";
  if (titleLower === 'tomorrow') return 'Show my schedule tomorrow';
  if (titleLower === 'overdue') return 'Show overdue tasks';
  if (titleLower === 'this week') return 'Show my schedule this week';
  if (titleLower === 'next week') return 'Show my schedule next week';
  if (titleLower === 'next month') {
    return 'Show every task due next month';
  }
  if (monthQuestion) return monthQuestion;
  if (titleLower === 'this month' || calendarSubView === 'MONTH') {
    return 'Show my appointments this month';
  }
  if (calendarSubView === 'AGENDA') {
    return 'Show my upcoming appointments';
  }

  return 'Show my upcoming appointments';
}

export function resolveScheduleWindowFromQuestion(
  question,
  now = new Date(),
  timeZone = resolveClientTimeZone(),
) {
  const normalized = normalizeLabel(question);
  const safeTimeZone = normalizeTimeZone(timeZone);

  if (/\btomorrow\b/.test(normalized)) {
    const tomorrow = addDaysInTimeZone(now, 1, safeTimeZone);
    return {
      windowStartMs: startOfDayMsForTimeZone(tomorrow, safeTimeZone),
      windowEndMs: endOfDayMsForTimeZone(tomorrow, safeTimeZone),
      timeZone: safeTimeZone,
    };
  }

  if (/\bnext week\b/.test(normalized)) {
    return buildNextWeekScheduleWindow(now, safeTimeZone);
  }

  if (/\bthis week\b/.test(normalized) || /\bweekly\b/.test(normalized)) {
    return buildThisWeekScheduleWindow(now, safeTimeZone);
  }

  if (/\bnext month\b/.test(normalized)) {
    return buildNextMonthScheduleWindow(now, safeTimeZone);
  }

  const namedMonthWindow = buildNamedMonthScheduleWindow(question, now, safeTimeZone);
  if (namedMonthWindow) {
    return namedMonthWindow;
  }

  if (/\bthis month\b/.test(normalized) || /\bmonthly\b/.test(normalized)) {
    return {
      windowStartMs: startOfMonthMsForTimeZone(now, safeTimeZone),
      windowEndMs: endOfMonthMsForTimeZone(now, safeTimeZone),
      timeZone: safeTimeZone,
    };
  }

  if (/\bnext quarter\b/.test(normalized)) {
    return buildNextQuarterScheduleWindow(now, safeTimeZone);
  }

  if (/\bthis quarter\b/.test(normalized)) {
    return buildThisQuarterScheduleWindow(now, safeTimeZone);
  }

  if (/\bnext year\b/.test(normalized)) {
    return buildNextYearScheduleWindow(now, safeTimeZone);
  }

  if (/\bthis year\b/.test(normalized) || /\bannual\b/.test(normalized)) {
    return buildThisYearScheduleWindow(now, safeTimeZone);
  }

  if (/\btoday\b/.test(normalized) || /\btonight\b/.test(normalized) || /\bon my plate\b/.test(normalized)) {
    return {
      windowStartMs: startOfDayMsForTimeZone(now, safeTimeZone),
      windowEndMs: endOfDayMsForTimeZone(now, safeTimeZone),
      timeZone: safeTimeZone,
    };
  }

  if (/\boverdue\b/.test(normalized)) {
    const lookbackDays = 365;
    const lookbackStart = addDaysInTimeZone(now, -lookbackDays, safeTimeZone);
    return {
      windowStartMs: startOfDayMsForTimeZone(lookbackStart, safeTimeZone),
      windowEndMs: startOfDayMsForTimeZone(now, safeTimeZone) - 1,
      timeZone: safeTimeZone,
    };
  }

  if (/\bupcoming\b/.test(normalized) || /\bagenda\b/.test(normalized)) {
    return buildUpcomingScheduleWindow(now, safeTimeZone);
  }

  return buildUpcomingScheduleWindow(now, safeTimeZone);
}

export function inferTimelineQuestionFromPlan(plan) {
  const title = cleanString(plan?.presentation?.view?.title).toLowerCase();

  if (title === 'today') return 'Show phases on a timeline today';
  if (title === 'this week') return 'Show phases on a timeline this week';
  if (title === 'next week') return 'Show phases on a timeline next week';
  if (title === 'this month') return 'Show phases on a timeline for this month';
  if (title === 'next month') return 'Show phases on a timeline for next month';
  if (title === 'this quarter') return 'Show phases on a timeline for this quarter';
  if (title === 'next quarter') return 'Show phases on a timeline for next quarter';
  if (title === 'this year') return 'Show phases on a timeline for this year';
  if (title === 'next year') return 'Show phases on a timeline for next year';

  return 'Show phases on a timeline';
}

export function resolveClientTimelineWindow(plan, now = new Date()) {
  const fromQuery = plan?.query?.scheduleWindow;
  if (fromQuery?.windowStartMs && fromQuery?.windowEndMs) {
    return fromQuery;
  }

  const title = cleanString(plan?.presentation?.view?.title).toLowerCase();
  const hasTimelineHorizon = [
    'today',
    'this week',
    'next week',
    'this month',
    'next month',
    'this quarter',
    'next quarter',
    'this year',
    'next year',
  ].includes(title);

  if (!hasTimelineHorizon) return null;

  return resolveScheduleWindowFromQuestion(
    inferTimelineQuestionFromPlan(plan),
    now,
    resolveClientTimeZone(),
  );
}

export function planRequestsOverdueSchedule(plan) {
  const title = cleanString(plan?.presentation?.view?.title).toLowerCase();
  if (title === 'overdue') return true;

  const agendaQueries = Array.isArray(plan?.query?.agendaQueries) ? plan.query.agendaQueries : [];
  return agendaQueries.some((agendaQuery) => (
    (Array.isArray(agendaQuery?.where) ? agendaQuery.where : [])
      .some((clause) => String(clause?.operator || '').trim().toLowerCase() === 'is_overdue')
  ));
}

export function resolveClientScheduleWindow(plan, now = new Date()) {
  const fromQuery = plan?.query?.scheduleWindow;
  if (fromQuery?.windowStartMs != null && fromQuery?.windowEndMs != null) {
    return fromQuery;
  }

  return resolveScheduleWindowFromQuestion(
    inferScheduleQuestionFromPlan(plan),
    now,
    resolveClientTimeZone(),
  );
}
